import type { Standing } from './standings.js';

/**
 * WHO APPEARS ON A STANDINGS TABLE SOMEBODY ELSE IS READING.
 *
 * Owner's rule, 2026-08-28: only players with a score above zero. A league session's table should be
 * a list of results, not a roster with most of it on nothing — before the second week that is
 * twenty-four rows of `0` and three rows of anything.
 *
 * IT IS A DISPLAY RULE AND NOTHING ELSE. Standings are computed fresh on every read, so this filters
 * what a surface SHOWS. It does not change how ranking works, what is stored, or who is in the
 * league. Ranks are computed over everybody and then survive the filter untouched — see below.
 *
 * IT LIVES HERE BECAUSE FOUR SURFACES NEED THE SAME ANSWER. The director's tab, the public page and
 * the image drawn from it all apply it; the player's own tab deliberately does not. Four screens
 * each deciding this separately would eventually disagree, and the one that disagreed would be the
 * public one, in front of players.
 */

/**
 * Has she scored anything yet.
 *
 * ⚠ THIS DOES NOT DISTINGUISH BEING AWAY FROM PLAYING AND SCORING NOTHING, and that is the owner's
 * open question — the first of five on backlog item 11. Both are zero, so the rule as stated hides
 * both. Hiding somebody who missed a session is kind; hiding somebody who turned up and had a bad
 * session is a judgement about her play. The engine records the difference already (`sessionsPlayed`
 * and `sessionsAbsent` are both on `Standing`), so answering it later is a change to this one
 * function and nothing else.
 */
export function hasScored(standing: Pick<Standing, 'points'>): boolean {
  return standing.points > 0;
}

/**
 * The table as everyone except the player herself sees it.
 *
 * RANKS ARE NOT RENUMBERED, and that is deliberate. If third place is hidden, the next player is
 * still fourth. Closing the numbers up would read more tidily and would mean a player on her own
 * screen — where nobody is hidden — sees a different rank for herself than the public page shows,
 * on the same day, from the same numbers. One of those two would be wrong and she would have no
 * way to tell which.
 *
 * IN PRACTICE NO GAP EVER APPEARS, and that is worth knowing before somebody "fixes" this. Ranking
 * is by points descending, so a player on nothing always sorts BELOW everybody who has scored — the
 * removed rows are therefore always contiguous at the bottom, and the published table reads 1, 2,
 * 3 … with nothing missing. Checked against the real demo league: hiding a mid-table player is not
 * a state that can occur, because zeroing her score is what moved her out of the middle.
 *
 * So this is a guarantee rather than a visible feature, and it holds even if a later scoring config
 * lets a zero-scorer outrank somebody on a negative total.
 *
 * `limit` is applied AFTER the filter, so "the top ten" is ten players who have actually scored
 * rather than ten counted from the top with some of them removed. The alternative shows eight rows
 * under a heading that says ten.
 */
export function publishedStandings<T extends Standing>(standings: T[], limit?: number): T[] {
  const scored = standings.filter(hasScored);
  return limit === undefined ? scored : scored.slice(0, limit);
}

/**
 * THE TOP FIVE, EACH ON AT LEAST 25 POINTS — what a player is shown of anybody else's standing
 * (Courtney, 2026-10-12: "Keep top 5 plus their own but add the at least 25 points for the top 5").
 * Everywhere a player reads a table: her Standings tab, Today, and the results emails.
 *
 * Places, not rows: everybody tied at fifth is in. Ranks are never renumbered and nobody is moved
 * up to fill a gap — points sort the table, so the players under 25 are always the bottom of it.
 * Unranked rows (rank 0: nothing approved yet) are never leaders.
 */
export const LEADER_PLACES = 5;
export const LEADER_MIN_POINTS = 25;

export function isLeader(standing: Pick<Standing, 'rank' | 'points'>): boolean {
  return standing.rank > 0 && standing.rank <= LEADER_PLACES && standing.points >= LEADER_MIN_POINTS;
}

/** The leaders, and her own row wherever it is and whatever her total — the only rows sent to her. */
export function leadersAndSelf<T extends Pick<Standing, 'rank' | 'points'>>(rows: T[], isSelf: (row: T) => boolean): T[] {
  return rows.filter((row) => isSelf(row) || isLeader(row));
}

/** "Sarah K." — how a player is named to other players (Courtney, 2026-10-12). */
export function shortName(firstName: string, lastName: string): string {
  const initial = lastName.trim().charAt(0).toUpperCase();
  return initial ? `${firstName.trim()} ${initial}.` : firstName.trim();
}

/**
 * WHICH SILENCE AN EMPTY TABLE IS.
 *
 * "Nobody has played yet" and "everybody is still on nothing" call for completely different things
 * from a director — the first is the calendar, the second is her score entry. A single empty
 * message covering both would send her to check the wrong one.
 *
 *   `not-started`   No session has been scored. The league has not begun.
 *   `nobody-scored` Scores exist and every one of them is zero. Rare, but it is the state on a
 *                   session where the sheet was entered as all zeros — which is usually a mistake
 *                   worth her seeing.
 *   `none`          Somebody is on the table.
 */
export type StandingsSilence = 'not-started' | 'nobody-scored' | 'none';

export function standingsSilence(all: Pick<Standing, 'points' | 'sessionsPlayed'>[]): StandingsSilence {
  if (all.some(hasScored)) return 'none';
  /* `sessionsPlayed` counts sessions with a score entered, absent or not. */
  const anythingEntered = all.some((standing) => standing.sessionsPlayed > 0);
  return anythingEntered ? 'nobody-scored' : 'not-started';
}
